/**
 * Configuration Manager - Save and load search configurations
 *
 * Provides functionality to:
 * - Save search configurations to localStorage
 * - Load saved configurations
 * - Manage configuration presets
 * - Export/import configurations as JSON
 */

export class ConfigurationManager {
    constructor() {
        this.storageKey = 'astro_saved_configs';
        this.preferencesKey = 'astro_user_preferences';
    }

    /**
     * Save a search configuration
     * @param {string} name - Configuration name
     * @param {Object} config - Configuration data
     * @returns {boolean} Success status
     */
    saveConfiguration(name, config) {
        try {
            const saved = this.getSavedConfigurations();

            saved[name] = {
                ...config,
                savedDate: new Date().toISOString(),
                version: '1.0'
            };

            localStorage.setItem(this.storageKey, JSON.stringify(saved));
            console.log(`Configuration "${name}" saved successfully`);
            return true;
        } catch (error) {
            console.error('Failed to save configuration:', error);
            return false;
        }
    }

    /**
     * Get all saved configurations
     * @returns {Object} Saved configurations
     */
    getSavedConfigurations() {
        try {
            const saved = localStorage.getItem(this.storageKey);
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error('Failed to load configurations:', error);
            return {};
        }
    }

    /**
     * Load a specific configuration
     * @param {string} name - Configuration name
     * @returns {Object|null} Configuration or null if not found
     */
    loadConfiguration(name) {
        try {
            const configs = this.getSavedConfigurations();
            return configs[name] || null;
        } catch (error) {
            console.error('Failed to load configuration:', error);
            return null;
        }
    }

    /**
     * Delete a configuration
     * @param {string} name - Configuration name
     * @returns {boolean} Success status
     */
    deleteConfiguration(name) {
        try {
            const saved = this.getSavedConfigurations();

            if (saved[name]) {
                delete saved[name];
                localStorage.setItem(this.storageKey, JSON.stringify(saved));
                console.log(`Configuration "${name}" deleted successfully`);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Failed to delete configuration:', error);
            return false;
        }
    }

    /**
     * Rename a configuration
     * @param {string} oldName - Current name
     * @param {string} newName - New name
     * @returns {boolean} Success status
     */
    renameConfiguration(oldName, newName) {
        try {
            const saved = this.getSavedConfigurations();

            if (saved[oldName] && !saved[newName]) {
                saved[newName] = {
                    ...saved[oldName],
                    savedDate: new Date().toISOString()
                };
                delete saved[oldName];
                localStorage.setItem(this.storageKey, JSON.stringify(saved));
                console.log(`Configuration renamed from "${oldName}" to "${newName}"`);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Failed to rename configuration:', error);
            return false;
        }
    }

    /**
     * Export configuration to JSON file
     * @param {string} name - Configuration name
     * @returns {boolean} Success status
     */
    exportConfiguration(name) {
        try {
            const config = this.loadConfiguration(name);

            if (!config) {
                console.error('Configuration not found');
                return false;
            }

            const dataStr = JSON.stringify(config, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `astro-config-${name}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log(`Configuration "${name}" exported successfully`);
            return true;
        } catch (error) {
            console.error('Failed to export configuration:', error);
            return false;
        }
    }

    /**
     * Export all configurations
     * @returns {boolean} Success status
     */
    exportAllConfigurations() {
        try {
            const configs = this.getSavedConfigurations();

            const dataStr = JSON.stringify(configs, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `astro-configs-all-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('All configurations exported successfully');
            return true;
        } catch (error) {
            console.error('Failed to export configurations:', error);
            return false;
        }
    }

    /**
     * Import configuration from JSON
     * @param {File} file - JSON file to import
     * @returns {Promise<boolean>} Success status
     */
    async importConfiguration(file) {
        try {
            const text = await file.text();
            const importedData = JSON.parse(text);

            // Check if it's a single config or multiple configs
            if (importedData.savedDate && importedData.version) {
                // Single configuration
                const name = file.name.replace('.json', '').replace('astro-config-', '');
                return this.saveConfiguration(name, importedData);
            } else {
                // Multiple configurations
                const saved = this.getSavedConfigurations();
                Object.keys(importedData).forEach(key => {
                    saved[key] = importedData[key];
                });
                localStorage.setItem(this.storageKey, JSON.stringify(saved));
                console.log('Configurations imported successfully');
                return true;
            }
        } catch (error) {
            console.error('Failed to import configuration:', error);
            return false;
        }
    }

    /**
     * Get list of configuration names
     * @returns {Array<string>} Configuration names
     */
    getConfigurationNames() {
        const configs = this.getSavedConfigurations();
        return Object.keys(configs).sort();
    }

    /**
     * Get configuration metadata
     * @param {string} name - Configuration name
     * @returns {Object|null} Metadata
     */
    getConfigurationMetadata(name) {
        const config = this.loadConfiguration(name);
        if (!config) return null;

        return {
            name: name,
            savedDate: config.savedDate,
            version: config.version,
            location: config.latitude && config.longitude ?
                `${config.latitude.toFixed(2)}, ${config.longitude.toFixed(2)}` : 'Unknown',
            direction: config.azimuth ? `${config.azimuth}°` : 'Unknown'
        };
    }

    /**
     * Save user preferences
     * @param {Object} preferences - User preferences
     * @returns {boolean} Success status
     */
    savePreferences(preferences) {
        try {
            localStorage.setItem(this.preferencesKey, JSON.stringify(preferences));
            console.log('User preferences saved');
            return true;
        } catch (error) {
            console.error('Failed to save preferences:', error);
            return false;
        }
    }

    /**
     * Get user preferences
     * @returns {Object} User preferences
     */
    getPreferences() {
        try {
            const prefs = localStorage.getItem(this.preferencesKey);
            return prefs ? JSON.parse(prefs) : this.getDefaultPreferences();
        } catch (error) {
            console.error('Failed to load preferences:', error);
            return this.getDefaultPreferences();
        }
    }

    /**
     * Get default preferences
     * @returns {Object} Default preferences
     */
    getDefaultPreferences() {
        return {
            defaultLocation: null,
            defaultFOV: 30,
            defaultMinDuration: 2,
            defaultMoonAvoidance: 30,
            preferredObjectTypes: [],
            autoLoadLastSearch: false,
            theme: 'default'
        };
    }

    /**
     * Clear all saved configurations
     * @returns {boolean} Success status
     */
    clearAllConfigurations() {
        try {
            localStorage.removeItem(this.storageKey);
            console.log('All configurations cleared');
            return true;
        } catch (error) {
            console.error('Failed to clear configurations:', error);
            return false;
        }
    }

    /**
     * Get storage usage information
     * @returns {Object} Storage info
     */
    getStorageInfo() {
        try {
            const configs = this.getSavedConfigurations();
            const configsSize = new Blob([JSON.stringify(configs)]).size;

            const prefs = this.getPreferences();
            const prefsSize = new Blob([JSON.stringify(prefs)]).size;

            return {
                configCount: Object.keys(configs).length,
                configsSize: configsSize,
                prefsSize: prefsSize,
                totalSize: configsSize + prefsSize,
                totalSizeKB: ((configsSize + prefsSize) / 1024).toFixed(2)
            };
        } catch (error) {
            console.error('Failed to get storage info:', error);
            return null;
        }
    }

    /**
     * Validate configuration data
     * @param {Object} config - Configuration to validate
     * @returns {Object} Validation result
     */
    validateConfiguration(config) {
        const errors = [];

        if (!config) {
            errors.push('Configuration is null or undefined');
            return { valid: false, errors };
        }

        // Check required fields
        if (config.latitude === undefined || isNaN(config.latitude)) {
            errors.push('Invalid latitude');
        }
        if (config.longitude === undefined || isNaN(config.longitude)) {
            errors.push('Invalid longitude');
        }
        if (config.azimuth === undefined || isNaN(config.azimuth)) {
            errors.push('Invalid azimuth');
        }

        // Check ranges
        if (config.latitude < -90 || config.latitude > 90) {
            errors.push('Latitude out of range');
        }
        if (config.longitude < -180 || config.longitude > 180) {
            errors.push('Longitude out of range');
        }
        if (config.azimuth < 0 || config.azimuth > 360) {
            errors.push('Azimuth out of range');
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }
}

export default ConfigurationManager;
